import {
  type ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { useNotification } from "@providers/NotificationProvider/useNotification";

import { NotificationTypeMappingText } from "@components/Texts";

import { Box } from "@design-system/Box/Box";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Table } from "@design-system/Table/Table";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

import {
  ResourceGroupNotificationsQuery,
  useResourceGroupNotificationsQuery,
} from "./ResourceGroupNotificationsTable.g";

const PAGE_SIZE = 30;

type NotificationNode = NonNullable<
  NonNullable<
    NonNullable<
      ResourceGroupNotificationsQuery["resourceGroupNotifications"]
    >["edges"][number]
  >["node"]
>;

const columnHelper = createColumnHelper<NotificationNode>();

type Props = {
  clientApplicationId: string;
  initialPageIndex?: number;
  pageSize?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
};

export const ResourceGroupNotificationsTable = ({
  clientApplicationId,
  initialPageIndex = 0,
  pageSize,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize || PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const notificationsQuery = useResourceGroupNotificationsQuery({
    clientApplicationId,
    first: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    includeResolved: false,
    sortBy:
      sorting.length > 0
        ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}`
        : undefined,
  });

  const { resolveNotification } = useNotification();

  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const totalCount =
    notificationsQuery.data?.resourceGroupNotifications?.totalCount ??
    undefined;

  const data = useMemo(() => {
    return (
      notificationsQuery.data?.resourceGroupNotifications?.edges
        .map((edge) => edge?.node)
        .filter((node) => !!node) ?? []
    );
  }, [notificationsQuery.data]);

  const handleResolveClick = useCallback(
    async (id: string) => {
      await resolveNotification(id);
    },
    [resolveNotification],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("application.resource.productName", {
          header: "Application",
          id: "resource__product_name",
          cell: (props) => {
            return (
              <Table.CompanyCell
                name={
                  props.row.original?.application?.resource?.productName ||
                  "Unknown Application"
                }
              />
            );
          },
          footer: undefined,
        }),
        columnHelper.accessor("employee.name", {
          header: "Employee",
          id: "employee__name",
          cell: (props) => {
            return props.row.original?.employee?.name || "Unknown";
          },
          footer: undefined,
        }),
        columnHelper.accessor("notificationType", {
          header: "Type",
          id: "notification_type",
          cell: (props) => {
            const value = props.getValue();
            if (!value) {
              return null;
            }
            return <NotificationTypeMappingText notificationType={value} />;
          },
          footer: undefined,
        }),
        columnHelper.accessor("createdAt", {
          header: "Created",
          id: "created_at",
          cell: (props) => {
            return <Table.DateCell date={new Date(props.getValue())} />;
          },
        }),
        columnHelper.display({
          id: "actions",
          header: "",
          meta: {
            sticky: true,
            actions: true,
          },
          cell: (props) => {
            return (
              <Box gap={2}>
                <Dropdown
                  trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
                >
                  <Dropdown.Option
                    label="Resolve"
                    icon="faCheck"
                    onSelect={() =>
                      handleResolveClick(props.row.original?.id || "")
                    }
                  />
                </Dropdown>
              </Box>
            );
          },
        }),
      ] as ColumnDef<NotificationNode>[],
    [handleResolveClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table
      columns={columns}
      data={data}
      pagination={pagination}
      sorting={sorting}
      onPaginationChange={handlePaginationChange}
      onSortingChange={setSorting}
      isLoading={notificationsQuery.isLoading}
      rowCount={totalCount}
      onPageSizeChange={handlePageSizeChange}
      showPageSizeDropdown={showPageSizeDropdown}
      showHeader={showHeader}
      showFooter={showFooter}
    />
  );
};
